import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import {
  competitionVideoPreviewTime,
  competitionVideoSrcWithPreviewTime,
  isCompetitionImageMedia,
  primeVideoElementFrame,
  resolveCompetitionMediaPoster,
} from "../../../utils/competitionEventMedia";

function VideoFramePreview({ src, poster, sx, objectFit = "cover" }) {
  const ref = useRef(null);
  const previewTime = competitionVideoPreviewTime();
  const [videoSrc, setVideoSrc] = useState(() =>
    competitionVideoSrcWithPreviewTime(src, previewTime)
  );

  useEffect(() => {
    setVideoSrc(competitionVideoSrcWithPreviewTime(src, previewTime));
    const el = ref.current;
    if (!el || !src) return;

    let cancelled = false;

    const run = async () => {
      const ok = await primeVideoElementFrame(el, previewTime);
      if (cancelled) return;
      if (!ok) {
        setVideoSrc(competitionVideoSrcWithPreviewTime(src, previewTime));
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [src, previewTime]);

  return (
    <Box
      component="video"
      ref={ref}
      src={videoSrc}
      poster={poster || undefined}
      muted
      playsInline
      preload="auto"
      aria-hidden
      sx={{
        width: "100%",
        height: "100%",
        objectFit,
        display: "block",
        pointerEvents: "none",
        backgroundColor: "#111",
        ...sx,
      }}
    />
  );
}

/**
 * Listing / modal preview — poster (upload or competition banner), else video frame.
 */
export default function CompetitionMediaThumbnail({
  item,
  sx,
  objectFit = "cover",
  competitionBanner = "",
}) {
  const isImage = isCompetitionImageMedia(item);
  const videoUrl = item?.video_url || "";
  const poster = resolveCompetitionMediaPoster(item, competitionBanner);
  const [posterFailed, setPosterFailed] = useState(false);

  useEffect(() => {
    setPosterFailed(false);
  }, [poster, videoUrl]);

  const mediaSx = {
    width: "100%",
    height: "100%",
    objectFit,
    display: "block",
    ...sx,
  };

  if (isImage && videoUrl) {
    return <Box component="img" src={videoUrl} alt="" sx={mediaSx} />;
  }

  if (videoUrl && poster && !posterFailed) {
    return (
      <Box
        component="img"
        src={poster}
        alt=""
        sx={mediaSx}
        onError={() => setPosterFailed(true)}
      />
    );
  }

  if (videoUrl) {
    return (
      <VideoFramePreview
        src={videoUrl}
        poster={poster || undefined}
        objectFit={objectFit}
        sx={sx}
      />
    );
  }

  if (poster && !posterFailed) {
    return (
      <Box
        component="img"
        src={poster}
        alt=""
        sx={mediaSx}
        onError={() => setPosterFailed(true)}
      />
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        bgcolor: "#111",
        ...sx,
      }}
    />
  );
}
